/*
**  rui-group-box.js
**
**  rui-group-box component
*/
import { LitElement, html, css } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
export class RuiGroupBox extends LitElement {
    static get is() {
        return 'rui-group-box';
    }
    static get properties() {
        return {
            titleText: { type: String },
            subtitleText: { type: String },
            isExpandable: { type: Object },
            isExpanded: { type: Object },
            accordionGroup: { type: String },
            accordionId: { type: String },
            containsError: { type: Object },
            isBusy: { type: Object },
            busyText: { type: String },
            isFullHeight: { type: Object }
        };
    }
    constructor() {
        super();
        this.titleText = '';
        this.subtitleText = '';
        this.isExpandable = false;
        this._isExpanded = false;
        this.accordionGroup = '';
        this.accordionId = '';
        this.containsError = false;
        this.isBusy = false;
        this.busyText = '';
        this.isFullHeight = false;
        this._isFixedLayoutVerticalScrolling = false;
    }
    connectedCallback() {
        super.connectedCallback();
        window.setTimeout(() => {
            this._scrollContainer = this.shadowRoot.querySelector('#rui-group-box-content');
            if (this._scrollContainer) {
                this.detectScrollability(this._scrollContainer);
                window.addEventListener('resize', () => this.detectScrollability(this._scrollContainer));
            }
        }, 100);
        window.addEventListener('rui-is-expanded-change', (e) => this.isExpandedChange(e));
        window.addEventListener('rui-fixed-layout-vertical-scrolling', (e) => this.fixedLayoutVerticalScrolling(e));
    }
    disconnectedCallback() {
        window.removeEventListener('rui-is-expanded-change', (e) => this.isExpandedChange(e));
        window.removeEventListener('rui-fixed-layout-vertical-scrolling', (e) => this.fixedLayoutVerticalScrolling(e));
        super.disconnectedCallback();
    }
    isExpandedChange(e) {
        // Collapse this accordion group box if a different group box in the same accordion group expands
        if (this.isExpandable && this.isExpanded && this.accordionGroup && this.accordionId) {
            if (e.detail.isExpanded === true &&
                e.detail.accordionGroup === this.accordionGroup &&
                e.detail.accordionId !== this.accordionId) {
                this.toggleExpanded();
            }
        }
    }
    fixedLayoutVerticalScrolling(e) {
        e.preventDefault();
        e.stopPropagation();
        this._isFixedLayoutVerticalScrolling = e.detail;
        this.requestUpdate();
    }
    detectScrollability(container) {
        if (container.scrollHeight > container.clientHeight) {
            container.classList.add('rui-group-box-content--scrollable');
        }
        else {
            container.classList.remove('rui-group-box-content--scrollable');
        }
    }
    // Custom setter for isExpanded property
    set isExpanded(newValue) {
        if (this._isExpanded !== newValue) {
            this._isExpanded = newValue;
            this.requestUpdate();
            // Fire group box expand/collapse event
            Promise.resolve().then(() => {
                let groupBoxExpandCollapseEvent = new CustomEvent('rui-is-expanded-change', {
                    detail: {
                        'isExpanded': this._isExpanded,
                        'accordionGroup': this.accordionGroup,
                        'accordionId': this.accordionId
                    },
                    bubbles: true,
                    composed: true
                });
                this.dispatchEvent(groupBoxExpandCollapseEvent);
            });
            // Animate the expand/collapse
            // Promise.resolve().then(() => {
            //     this.expandCollapseContent();
            // });
        }
    }
    // Custom getter for isExpanded property
    get isExpanded() {
        return this._isExpanded;
    }
    // Toggle expanded state
    toggleExpanded() {
        // Toggle expanded
        this.isExpanded = !this.isExpanded;
    }
    // 10/21/2021: I'M COMMENTING OUT ALL THE EXPAND/COLLAPSE JS IN HERE
    // BECAUSE I CAN'T GET THIS TO SMOOTHLY WORK. WE ORIGINALLY USED AN
    // ANIMATION BUNDLED FROM EITHER BOOTSTRAP OR ANGULAR, NOT SOMETHING
    // THAT WE CAN DROP INTO A WEB COMPONENT IN EITHER CASE. WE WILL
    // RETURN TO THIS LATER AND SEE IF WE CAN GET THIS WORKING SMOOTHLY.
    // Expand or collapse the content for expandable group boxes
    // expandCollapseContent() {
    //     if (this.isExpandable) {
    //         if (this.isExpanded) {
    //             this.expandContent();
    //         } else {
    //             this.collapseContent();
    //         }
    //     }
    // }
    // Expand the group box content
    // expandContent() {
    //     const el = this.shadowRoot.querySelector('#content');
    //     if (el) {
    //         // Get the height of the element's inner content, regardless of its actual size
    //         const sectionHeight = el.scrollHeight;
    //         // Have the element transition to the height of its inner content
    //         el.style.height = sectionHeight + 'px';
    //         // When the next css transition finishes (which should be the one we just triggered)
    //         el.addEventListener('transitionend', (e) => {
    //             // Remove "height" from the element's inline styles, so it can return to its initial value
    //             el.style.height = null;
    //         }, { once: true });
    //     }
    // }
    // Collapse the group box content
    // collapseContent() {
    //     const el = this.shadowRoot.querySelector('#content');
    //     if (el) {
    //         // Get the height of the element's inner content, regardless of its actual size
    //         const sectionHeight = el.scrollHeight;
    //         // Temporarily disable all css transitions
    //         const elementTransition = el.style.transition;
    //         el.style.transition = '';
    //         // On the next frame (as soon as the previous style change has taken effect),
    //         // explicitly set the element's height to its current pixel height, so we 
    //         // aren't transitioning out of 'auto'
    //         requestAnimationFrame(() => {
    //             el.style.height = sectionHeight + 'px';
    //             el.style.transition = elementTransition;
    //             // On the next frame (as soon as the previous style change has taken effect),
    //             // have the element transition to height: 0
    //             requestAnimationFrame(() => {
    //                 el.style.height = 0 + 'px';
    //             });
    //         });
    //     }
    // }
    render() {
        const outerClasses = {
            ['rui-group-box']: true,
            ['rui-group-box-full-height']: this.isFullHeight,
            ['rui-group-box-collapsed']: this.isExpandable && !this.isExpanded,
            ['rui-group-box-error']: this.containsError,
            ['rui-group-box-fixed-layout-vertical-scrolling']: this._isFixedLayoutVerticalScrolling
        };
        const containerClasses = {
            ['rui-group-box-container']: true,
            ['rui-group-box-container-full-height']: this.isFullHeight
        };
        const iconClasses = {
            ['rui-group-box-header-icon']: true,
            ['rui-group-box-header-icon-expanded']: this.isExpanded
        };
        let contentStyles = {};
        // if (this.isExpandable) {
        //     contentStyles.height = '0px';
        // };
        return html `
            <section class="${classMap(outerClasses)}" aria-busy="${this.isBusy}">
                <div class="${classMap(containerClasses)}">
                    ${this.isExpandable || this.titleText || this.subtitleText ? html `
                        <header class="rui-group-box-header">
                            <h3 class="rui-group-box-title">
                                ${this.isExpandable ? html `
                                    <button class="rui-group-box-header-container rui-group-box-expand-toggle" type="button" @click="${this.toggleExpanded}" tabindex="0">
                                        <rui-icon iconType="window-dropdown" context="${this.containsError ? 'group-box-arrow-error' : 'group-box-arrow'}" class="${classMap(iconClasses)}"></rui-icon>
                                        <div class="rui-group-box-title-text">
                                            ${this.titleText}
                                        </div>
                                        ${this.subtitleText ? html `<span class="rui-group-box-subtitle">${this.subtitleText}</span>` : ''}
                                    </button>
                                ` : ''}
                                ${!this.isExpandable ? html `
                                    <div class="rui-group-box-header-container">
                                        <div class="rui-group-box-title-text">
                                            ${this.titleText}
                                        </div>
                                        ${this.subtitleText ? html `<span class="rui-group-box-subtitle">${this.subtitleText}</span>` : ''}
                                    </div>
                                ` : ''}
                            </h3>
                        </header>
                    ` : ''}
                    <article id="content" class="rui-group-box-body-wrapper" style="${styleMap(contentStyles)}">
                        <div class="rui-group-box-body">
                            <slot name="group-box-toolbar-below"></slot>
                            <div id="rui-group-box-content" class="rui-group-box-content">
                                <slot></slot>
                            </div>
                        </div>
                        ${this.isBusy ? html `<rui-busy-indicator text="${this.busyText}" context="group-box"></rui-busy-indicator>` : ''}
                    </article>
                </div>
            </section>
        `;
    }
    static get styles() {
        return css `
            :host {
                display: block;
                margin-bottom: 10px;
            }
            
            :host([isFullHeight="true"]) {
                height: 100%;
            }
            
            ::selection {
                background: var(--rui-btn-primary-bg, #085ce5);
                color: var(--rui-btn-primary-fg, #fff);
            }
            
            .rui-group-box {
                font-family: var(--rui-font-family, "Noto Sans", Roboto, Arial, -apple-system, system-ui, sans-serif);
                margin-bottom: 0;
            }
            
            .rui-group-box-full-height {
                height: 100%;
            }
            
            .rui-group-box-fixed-layout-vertical-scrolling {
                margin-bottom: 10px;
            }
            
            .rui-group-box-container {
                border: 1px solid var(--rui-line, #e0e8f5);
                border-radius: 8px;
                display: flex;
                flex-direction: column;
                position: relative;
            }
            
            .rui-group-box-container-full-height {
                height: 100%;
                overflow: hidden;
            }
            
            .rui-group-box-error .rui-group-box-container {
                border-color: var(--rui-btn-destructive-border, #e13329);
            }
            
            .rui-group-box-header {
                background: var(--rui-group-box-bg, transparent);
                border-radius: 7px 7px 0 0;
                flex: 0 0 auto;
            }

            .rui-group-box-collapsed .rui-group-box-header {
                border-radius: 7px;
            }
            
            .rui-group-box-error .rui-group-box-header {
                background-color: var(--rui-btn-destructive-bg, #fff);
            }
            
            .rui-group-box-header-container {
                display: flex;
                padding: 6px 12px;
            }
            
            .rui-group-box-expand-toggle {
                cursor: pointer;
            }
            
            .rui-group-box-title {
                color: var(--rui-text-bright, #203660);
                font-family: var(--rui-font-family-chrome, Gellix, "Noto Sans", Roboto, Arial, -apple-system, system-ui, sans-serif);
                font-size: 14px;
                font-weight: 600;
                margin: 0;
                width: 100%;
                -webkit-font-smoothing: antialiased;
            }

            
            .rui-group-box-title > button {
                background: transparent;
                border: none;
                color: var(--rui-text-bright, #203660);
                font-family: var(--rui-font-family-chrome, Gellix, "Noto Sans", Roboto, Arial, -apple-system, system-ui, sans-serif);
                font-size: 13px;
                line-height: 20px;
                text-align: left;
                width: 100%;
            }
            
            .rui-group-box-error .rui-group-box-title > a, .rui-group-box-error .rui-group-box-title > button {
                color: var(--rui-btn-destructive-fg, #e13329);
            }
            
            .rui-group-box-error .rui-group-box-title {
                color: var(--rui-btn-destructive-fg, #e13329);
            }
            
            .rui-group-box-title-text {
                flex: 1 0 0%;
                font-weight: 600;
            }
            
            .rui-group-box-subtitle {
                color: var(--rui-text-regular, #09175b);
                flex: 0 1 auto;
                font-size: 14px;
                font-weight: 500;
                opacity: var(--rui-group-box-subtitle-opacity, 0.66);
                text-align: right;
                -webkit-font-smoothing: antialiased;
            }
            
            .rui-group-box-header-icon {
                display: inline-block;
                flex: 0 0 auto;
                height: 20px;
                margin: 1px 7px 0 0;
                transform: rotate(-90deg);
                transition: .2s all ease-out;
            }
            
            .rui-group-box-header-icon-expanded {
                transform: rotate(0);
            }
            
            .rui-group-box-body-wrapper {
                height: auto;
                flex: 1 0 0%;
                min-height: 0px;
                overflow: hidden;
                position: relative;
                transition: height 0.2s ease-out;
            }
            
            .rui-group-box-collapsed .rui-group-box-body-wrapper {
                display: none;
            }

            .rui-group-box-body {
                border-color: var(--rui-line, #e0e8f5);
                padding: 0;
            }
            
            .rui-group-box-container-full-height .rui-group-box-body {
                overflow: hidden;
                height: 100%;
                display: flex;
                flex-direction: column;
            }
            
            .rui-group-box-content {
                font-family: var(--rui-font-family, "Noto Sans", Roboto, Arial, -apple-system, system-ui, sans-serif);
                font-size: var(--rui-font-size, 13px);
                font-weight: var(--rui-font-weight, 400);
                padding: 15px;
            }
                
            .rui-group-box-content.rui-group-box-content--scrollable {
                margin-right: 2px;
                padding-right: 10px;
            }
            
            .rui-group-box-container-full-height .rui-group-box-content {
                flex: 1 0 0%;
                overflow: hidden auto;
                
            }
                
            ::-webkit-scrollbar {
                height: 7px;
                width: 7px;
            }
                            
            ::-webkit-scrollbar-track {
                background-color: var(--rui-scrollbar-track, transparent);
                border-radius: 3.5px;
            }

            ::-webkit-scrollbar-thumb {
                background-color: var(--rui-scrollbar-thumb, rgba(0, 0, 0, 0.2));
                border-radius: 3.5px;
            }
            
            @media (prefers-reduced-motion: reduce) {
                .rui-group-box-header-icon {
                    transition: none;
                }
            
                .rui-group-box-body-wrapper {
                    transition: none;
                }
            }
            
            @media screen and (max-width: 767px) {
                .rui-display-block-header {
                    flex-direction: column;
                }
            }
            
            @media print {
                .rui-group-box {
                    border: none;
                    padding-left: 0;
                    padding-right: 0;
                }
            
                .rui-group-box-container,
                .rui-group-box-header,
                .rui-group-box-title,
                .rui-group-box-subtitle {
                    background-color: transparent;
                    color: #000000;
                }
            }
        `;
    }
}
customElements.get(RuiGroupBox.is) || customElements.define(RuiGroupBox.is, RuiGroupBox);
